import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { 
  X, Megaphone, Rocket, AlertTriangle, 
  ShieldAlert, ChevronLeft 
} from 'lucide-react'
import { useLocation } from 'react-router-dom'
import { useTheme } from '../../context/ThemeContext'

const GlobalAnnouncements = () => {
    const { t } = useTheme()
    const [announcements, setAnnouncements] = useState<any[]>([])
    const [currentIndex, setCurrentIndex] = useState(0)
    const [isVisible, setIsVisible] = useState(false)
    const location = useLocation()

    useEffect(() => {
        const fetchAnnouncements = async () => {
            try {
                const res = await fetch('/api/core/announcements/active/', { credentials: 'include' })
                if (res.ok) {
                    const data = await res.json()
                    const dismissedIds = JSON.parse(localStorage.getItem('dismissed_announcements') || '[]')
                    
                    // Filter out already dismissed ones
                    const filtered = (data || []).filter((a: any) => !dismissedIds.includes(a.id))
                    
                    if (filtered.length > 0) {
                        setAnnouncements(filtered)
                        setIsVisible(true)
                    }
                }
            } catch (error) {
                console.error('Failed to fetch global announcements:', error)
            }
        }

        fetchAnnouncements()
    }, [location.pathname])

    if (!isVisible || announcements.length === 0) return null

    const current = announcements[currentIndex]

    const getTypeStyles = (type: string) => {
        switch (type) {
            case 'release': return { icon: <Rocket className="w-4 h-4" />, color: 'bg-primary-600', label: t('announcements.types.release', 'New Update') }
            case 'maintenance': return { icon: <ShieldAlert className="w-4 h-4" />, color: 'bg-warning-600', label: t('announcements.types.maintenance', 'Scheduled Maintenance') }
            case 'warning': return { icon: <AlertTriangle className="w-4 h-4" />, color: 'bg-secondary-600', label: t('announcements.types.warning', 'Warning') }
            default: return { icon: <Megaphone className="w-4 h-4" />, color: 'bg-neutral-800', label: t('announcements.types.default', 'Announcement') }
        }
    }

    const styles = getTypeStyles(current.announcement_type)

    const markAsDismissed = (id: number) => {
        const dismissedIds = JSON.parse(localStorage.getItem('dismissed_announcements') || '[]')
        if (!dismissedIds.includes(id)) {
            localStorage.setItem('dismissed_announcements', JSON.stringify([...dismissedIds, id]))
        }
    }

    const handleNext = () => {
        markAsDismissed(current.id)
        if (currentIndex < announcements.length - 1) {
            setCurrentIndex(currentIndex + 1)
        } else {
            setIsVisible(false) 
        }
    }

    const handleCloseAll = () => {
        announcements.forEach(a => markAsDismissed(a.id))
        setIsVisible(false)
    }

    return (
        <AnimatePresence>
            {isVisible && (
                <motion.div 
                    initial={{ y: -100, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    exit={{ y: -100, opacity: 0 }}
                    className="fixed top-0 left-0 right-0 z-[100] p-4 flex justify-center pointer-events-none"
                >
                    <div className="bg-surface rounded-[24px] shadow-2xl border border-neutral-100 p-2 pl-4 flex items-center gap-4 max-w-2xl w-full pointer-events-auto">
                        <div className={`w-10 h-10 ${styles.color} rounded-xl flex items-center justify-center text-white shrink-0 shadow-lg shadow-black/5`}>
                            {styles.icon}
                        </div>
                        
                        <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-0.5">
                                <span className="text-[10px] font-black uppercase tracking-widest text-neutral-400">{styles.label}</span>
                                <span className="w-1 h-1 rounded-full bg-neutral-200"></span>
                                <h4 className="text-sm font-black text-neutral-900 truncate">{current.title}</h4>
                            </div>
                            <p className="text-xs font-bold text-neutral-500 truncate leading-relaxed">
                                {current.content}
                            </p>
                        </div>

                        <div className="flex items-center gap-2">
                            {announcements.length > 1 && (
                                <span className="text-[10px] font-black text-neutral-300 ml-2">
                                    {currentIndex + 1} / {announcements.length}
                                </span>
                            )}
                            <button 
                                onClick={handleNext}
                                className="h-9 px-4 bg-neutral-50 hover:bg-neutral-100 rounded-xl text-xs font-black text-neutral-900 transition-all flex items-center gap-2"
                            >
                                {currentIndex < announcements.length - 1 
                                    ? t('announcements.actions.next', 'Next') 
                                    : t('announcements.actions.dismiss', 'Got it')}
                                <ChevronLeft className="w-3 h-3 rotate-180" />
                            </button>
                            <button 
                                onClick={handleCloseAll}
                                className="w-9 h-9 flex items-center justify-center text-neutral-300 hover:text-secondary-500 transition-colors"
                            >
                                <X className="w-4 h-4" />
                            </button>
                        </div>
                    </div>
                </motion.div>
            )}
        </AnimatePresence>
    )
}

export default GlobalAnnouncements
